// PWA install plumbing.
//
// Why this exists: FOS is a PWA and has always been installable, but nothing
// ever asked. On iOS, web push is only permitted from a PWA that the user has
// added to their home screen — so install reach is the hard ceiling on every
// future reminder/nudge feature. No install, no prompt, no wear log.
//
// This module is deliberately dumb: detection + dismissal memory + one event
// name. No network, no writes, no React.

/** Fired by useWearLog.addLog after any successful wear-log insert. */
export const WEAR_LOGGED_EVENT = 'fos:wear-logged'

const DISMISS_KEY = 'fos_install_prompt_dismissed_v1'
const GUIDE_SEEN_KEY = 'fos:install-guide-seen'

// localStorage throws in Safari private mode and in some embedded webviews.
// A telemetry-ish nicety must never take the app down.
function safeGet(key) {
  try { return window.localStorage.getItem(key) } catch { return null }
}
function safeSet(key, value) {
  try { window.localStorage.setItem(key, value) } catch { /* no-op */ }
}

/** True when running as an installed PWA rather than a browser tab. */
export function isStandalone() {
  if (typeof window === 'undefined') return false
  const mediaStandalone = window.matchMedia?.('(display-mode: standalone)')?.matches === true
  const iosStandalone = window.navigator?.standalone === true // iOS Safari only
  return mediaStandalone || iosStandalone
}

/** 'standalone' | 'browser' — the measurement that decides the native question later. */
export function displayMode() {
  return isStandalone() ? 'standalone' : 'browser'
}

function isIOS() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  if (/iPad|iPhone|iPod/.test(ua)) return true
  // iPadOS 13+ masquerades as desktop Safari; touch points disambiguate.
  return navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1
}

/**
 * True only for real iOS Safari.
 *
 * Matters because Safari never fires `beforeinstallprompt` — the only path is
 * a manual Share → Add to Home Screen, so we show instructions instead of a
 * button. Chrome/Firefox/Edge on iOS and in-app webviews (Instagram, Facebook)
 * CANNOT add to home screen at all, so showing them instructions would be
 * telling the user to do something impossible.
 */
export function isIOSSafari() {
  if (!isIOS()) return false
  const ua = navigator.userAgent || ''
  const isSafariShell = /Safari/.test(ua)
  const isOtherBrowserOrWebview = /CriOS|FxiOS|EdgiOS|OPiOS|FBAN|FBAV|Instagram|Line|Twitter|LinkedInApp/.test(ua)
  return isSafariShell && !isOtherBrowserOrWebview
}

export function isInstallPromptDismissed() {
  return safeGet(DISMISS_KEY) === '1'
}

export function markInstallPromptDismissed() {
  safeSet(DISMISS_KEY, '1')
}

// The first-visit install banner is answered per account, not per device: a
// second person signing in on the same browser still gets asked once.
function installGuideSeenKey(userId) {
  return userId ? `${GUIDE_SEEN_KEY}:${userId}` : null
}

/** No user means no banner, so an anonymous caller reads as already answered. */
export function isInstallGuideSeen(userId) {
  const key = installGuideSeenKey(userId)
  return key ? safeGet(key) === '1' : true
}

export function markInstallGuideSeen(userId) {
  const key = installGuideSeenKey(userId)
  if (key) safeSet(key, '1')
}

// `beforeinstallprompt` fires once per page load and its event is single-use,
// so it is captured in exactly one place and every install surface (the
// post-wear prompt, the on-demand guide) reads and consumes this one copy.
let deferredInstallPrompt = null
let installPromptCaptureTarget = null
const installPromptSubscribers = new Set()

function notifyInstallPromptSubscribers() {
  for (const subscriber of installPromptSubscribers) subscriber()
}

/** Idempotent. Call as early as possible: the event may fire before React mounts. */
export function startInstallPromptCapture(target = typeof window === 'undefined' ? null : window) {
  if (!target || installPromptCaptureTarget) return
  installPromptCaptureTarget = target
  target.addEventListener('beforeinstallprompt', event => {
    // Stash it so we raise it on OUR trigger, not the browser's mini-infobar.
    event.preventDefault()
    deferredInstallPrompt = event
    notifyInstallPromptSubscribers()
  })
  target.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null
    notifyInstallPromptSubscribers()
  })
}

export function getDeferredInstallPrompt() {
  return deferredInstallPrompt
}

export function subscribeInstallPrompt(subscriber) {
  installPromptSubscribers.add(subscriber)
  return () => { installPromptSubscribers.delete(subscriber) }
}

/** Raises the captured native prompt once. Resolves to its outcome, or null when there was none. */
export async function promptDeferredInstall() {
  const event = deferredInstallPrompt
  if (!event) return null
  deferredInstallPrompt = null // a captured prompt is single-use, for every surface
  notifyInstallPromptSubscribers()
  event.prompt()
  const { outcome } = await event.userChoice
  return outcome
}

/** Single choke point so every wear-log call site gets the trigger for free. */
export function emitWearLogged() {
  if (typeof window === 'undefined') return
  try {
    window.dispatchEvent(new CustomEvent(WEAR_LOGGED_EVENT))
  } catch { /* never let a nicety break a save */ }
}
